/* eslint-disable @typescript-eslint/no-magic-numbers */

import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InputComponent, TooltipModule, CheckboxComponent, ContextMenuHeaderButton, GenerateTextAction } from '@libs/ui-lib';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { ProvisionalStatus, EnhanceMessagePromptType, EnhancePatientNotePromptType, EnhancePromptType } from '@ffd/api-client';
import { ConnectedPosition, Overlay, OverlayModule } from '@angular/cdk/overlay';
import { CdkMenuModule } from '@angular/cdk/menu';
import { FfdNoteItem, FfdNotesScreenService } from '@libs/ffd-notes';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, skip, Subscription } from 'rxjs';
import { Clipboard } from '@angular/cdk/clipboard';
import { localStorageKey } from '@libs/utils';
import { Store } from '@ngrx/store';
import { selectCurrentUserProfile } from '@libs/store';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { TextareaWithAIComponent, ENHANCE_ENDPOINT_TYPE } from '@libs/textarea-with-ai';
import { FfdNotesEnhancementType, TagEvent, TagPusherService } from '@libs/shared-services';

const TITLE_DEBOUNCE_MS = 500;

@Component({
  selector: 'lib-ffd-note-header',
  standalone: true,
  imports: [
    CommonModule,
    SvgIconComponent,
    TranslateModule,
    ReactiveFormsModule,
    MatMenuModule,
    TooltipModule,
    OverlayModule,
    CdkMenuModule,
    InputComponent,
    TextareaWithAIComponent,
    CheckboxComponent,
  ],
  templateUrl: './ffd-note-header.component.html',
  styleUrl: './ffd-note-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: ENHANCE_ENDPOINT_TYPE,
      useValue: 'notes',
    },
  ],
  animations: [
    trigger('iconTransition', [
      state(
        'default',
        style({
          opacity: 1,
          transform: 'scale(1) rotate(0deg)',
        }),
      ),
      state(
        'copied',
        style({
          opacity: 1,
          transform: 'scale(1) rotate(0deg)',
        }),
      ),
      transition('default <=> copied', [
        animate(
          '200ms ease-out',
          style({
            opacity: 0.5,
            transform: 'scale(0.8) rotate(-15deg)',
          }),
        ),
        animate(
          '200ms ease-in',
          style({
            opacity: 1,
            transform: 'scale(1.1) rotate(5deg)',
          }),
        ),
        animate(
          '150ms ease-out',
          style({
            opacity: 1,
            transform: 'scale(1) rotate(0deg)',
          }),
        ),
      ]),
    ]),
  ],
})
export class FfdNoteHeaderComponent implements OnInit {
  private readonly overlay = inject(Overlay);
  private readonly destroyRef = inject(DestroyRef);
  private readonly clipboard = inject(Clipboard);
  private readonly store = inject(Store);
  private readonly tagPusherService = inject(TagPusherService);
  private readonly ffdNotesScreenService = inject(FfdNotesScreenService);

  public ProvisionalStatus = ProvisionalStatus;

  public note = input.required<FfdNoteItem>();
  public inEditMode = input<boolean>(false);
  public isNoteSaving = input<boolean>(false);
  public isNoteSaved = input<boolean>(false);

  public readonly editNote = output<void>();
  public readonly cancelEditMode = output<void>();
  public readonly deleteNote = output<void>();
  public readonly emptyAreaClicked = output<void>();
  public readonly noteSave = output<FfdNoteItem>();

  public readonly currentNote = signal<FfdNoteItem | null>(null);

  protected readonly dateTimeFormat = 'MMM d, h:mm a';
  protected noteTitleFormControl = new FormControl<string | null>(null);
  protected noteContentFormControl = new FormControl<string | null>(null);

  protected isCopyOverlayOpen = signal(false);
  protected isNoteCopied = signal(false);
  protected doNotShowAgainChecked = signal(false);
  protected userProfileId = this.store.selectSignal(selectCurrentUserProfile)()?.id;

  protected overlayPositionStrategy: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 20,
      offsetX: 0,
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -10,
      offsetX: 0,
    },
  ];
  protected contextMenuHeaderConfig: ContextMenuHeaderButton[] = [
    {
      text: 'notes_soap_button',
      type: GenerateTextAction.NoteSoap,
    },
    {
      text: 'notes_concise_button',
      type: GenerateTextAction.NoteConcise,
    },
    {
      text: 'notes_enhance_note_button',
      type: GenerateTextAction.NoteRewrite,
    },
  ];

  protected contextMenuConfig: ContextMenuHeaderButton[] = [
    {
      text: 'notes_concise_button',
      type: GenerateTextAction.NoteConcise,
    },
    {
      text: 'notes_enhance_note_button',
      type: GenerateTextAction.NoteRewrite,
    },
  ];

  public readonly scrollStrategy = this.overlay.scrollStrategies.noop();
  private noteTitleSubscription: Subscription | null = null;
  private noteContentSubscription: Subscription | null = null;

  constructor() {
    effect(() => {
      const note = this.note();
      const currentNote = this.currentNote();
      const isContentSame = currentNote ? this.noteContentFormControl.value === currentNote.content : true;
      const isTitleSame = currentNote ? this.noteTitleFormControl.value === currentNote.title : true;

      if (currentNote && note.id !== currentNote.id && (!isContentSame || !isTitleSame)) {
        this.onNoteSave({
          ...currentNote,
          title: this.noteTitleFormControl.value ?? currentNote.title,
          content: this.noteContentFormControl.value ?? currentNote.content,
        });
      }
    });
    effect(() => {
      const note = this.note();
      const currentNote = this.currentNote();

      if (note.id !== currentNote?.id) {
        this.noteTitleFormControl.setValue(note.title ?? null, { emitEvent: false });
        this.noteContentFormControl.setValue(note.content ?? null, { emitEvent: false });
        this.listenToTitleChanges();
        this.listenToContentChanges();
        this.currentNote.set(note);
      }
    });
  }

  public ngOnInit(): void {
    this.listenToTitleChanges();
    this.listenToContentChanges();
  }

  public emptyAreaClick(event: Event): void {
    event.stopPropagation();
  }

  public menuClicked(event: Event): void {
    event.stopPropagation();
  }

  protected onToggleEditMode(): void {
    if (this.inEditMode()) {
      this.cancelEditMode.emit();
    } else {
      this.editNote.emit();
    }
  }

  protected onDeleteNoteClick(): void {
    this.deleteNote.emit();
  }

  protected onCopyNote(event: Event): void {
    event.stopPropagation();

    const noteContent = this.note().content || '';
    this.clipboard.copy(noteContent);
    this.isNoteCopied.set(true);
    this.tagPusherService.pushTag({
      event: TagEvent.FfdNotesNoteCopied,
    });

    // Check if user has opted to not show the overlay
    const hideOverlay = localStorage.getItem(`${localStorageKey.hideFfdNoteCopiedOverlay}_${this.userProfileId}`) === 'true';

    if (!hideOverlay) {
      this.isCopyOverlayOpen.set(true);
    }

    // Reset copied state after 3 seconds
    setTimeout(() => {
      this.isNoteCopied.set(false);
    }, 3000);

    // send request to update hasBeenCopied to true
    const noteToUpdate: FfdNoteItem = {
      id: this.note().id,
      title: this.note().title,
      content: this.note().content,
      date: this.note().date,
      hasBeenCopied: true,
    };

    this.ffdNotesScreenService.updateNote(noteToUpdate);
  }

  protected closeCopyOverlay(): void {
    this.isCopyOverlayOpen.set(false);
    this.doNotShowAgainChecked.set(false);
  }

  protected onDoNotShowAgainChange(checked: boolean): void {
    this.doNotShowAgainChecked.set(checked);
    if (checked) {
      localStorage.setItem(`${localStorageKey.hideFfdNoteCopiedOverlay}_${this.userProfileId}`, 'true');
    } else {
      localStorage.removeItem(`${localStorageKey.hideFfdNoteCopiedOverlay}_${this.userProfileId}`);
    }
  }

  protected onGotItClick(): void {
    this.closeCopyOverlay();
  }

  protected onNoteSave(saveData: FfdNoteItem): void {
    this.noteSave.emit(saveData);
  }

  protected onEnhanceUsed(enhanceType: EnhancePatientNotePromptType | EnhancePromptType | EnhanceMessagePromptType): void {
    const enhancement = this.mapEnhanceTypeToAnalytics(enhanceType);
    if (enhancement) {
      this.tagPusherService.pushTag({
        event: TagEvent.FfdNotesNoteEditing,
        enhancement,
      });
    }
  }

  protected onVoiceTypingStatusChange(isRecording: boolean): void {
    if (!isRecording) {
      return;
    }

    this.tagPusherService.pushTag({
      event: TagEvent.FfdNotesNoteEditing,
      enhancement: FfdNotesEnhancementType.SecureVoiceTyping,
    });
  }

  private mapEnhanceTypeToAnalytics(
    enhanceType: EnhancePatientNotePromptType | EnhancePromptType | EnhanceMessagePromptType,
  ): FfdNotesEnhancementType | null {
    switch (enhanceType) {
      case EnhancePromptType.SOAP: {
        return FfdNotesEnhancementType.Soap;
      }
      case EnhancePromptType.ENHANCE: {
        return FfdNotesEnhancementType.Rewrite;
      }
      case EnhancePromptType.CONCISE: {
        return FfdNotesEnhancementType.Concise;
      }
      default: {
        return null;
      }
    }
  }

  private listenToTitleChanges() {
    this.noteTitleSubscription?.unsubscribe();
    this.noteTitleSubscription = this.noteTitleFormControl.valueChanges
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef), distinctUntilChanged(), debounceTime(TITLE_DEBOUNCE_MS))
      .subscribe((newTitle) => {
        const currentNote = this.note();
        if (newTitle !== currentNote.title) {
          this.tagPusherService.pushTag({
            event: TagEvent.FfdNotesNotePatientAdded,
          });

          this.onNoteSave({
            ...currentNote,
            title: newTitle ?? currentNote.title,
          });
        }
      });
  }

  private listenToContentChanges() {
    this.noteContentSubscription?.unsubscribe();
    this.noteContentSubscription = this.noteContentFormControl.valueChanges
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef), distinctUntilChanged(), debounceTime(TITLE_DEBOUNCE_MS))
      .subscribe((newContent) => {
        const currentNote = this.note();
        if (newContent !== currentNote.content) {
          this.onNoteSave({
            ...currentNote,
            content: newContent ?? currentNote.content,
          });
        }
      });
  }
}
